/**
 * Minting Dapp Registry
 *
 * A singleton registry for managing minting dapp plugins.
 * Allows registration, lookup, and enumeration of available dapps.
 *
 * Usage:
 * ```typescript
 * import { mintingDappRegistry } from './registry';
 *
 * // Register a dapp
 * mintingDappRegistry.register({
 *   id: 'mint-nft',
 *   name: 'Mint NFT',
 *   type: 'internal',
 *   component: MyComponent,
 * });
 *
 * // Get a dapp
 * const dapp = mintingDappRegistry.get('mint-nft');
 *
 * // Get all dapps
 * const all = mintingDappRegistry.getAll();
 * ```
 *
 * @module minting/registry
 * @category Minting Framework
 */

import { MintingDapp } from './types';

/**
 * Registry class for managing minting dapp plugins.
 * Provides CRUD operations for dapp registration and lookup.
 */
export class MintingDappRegistry {
  private dapps: Map<string, MintingDapp> = new Map();
  private listeners = new Set<() => void>();

  /**
   * Subscribe to registry mutations.
   *
   * The registry is populated asynchronously from the backend API in App.tsx,
   * which runs after the first render. Consumers previously read it through a
   * useMemo keyed only on the dapp id, so a late register() left the UI stuck
   * on "Minting Dapp Not Found" even though the dapp was present. Notifying
   * subscribers lets those components re-render once the data lands.
   *
   * @returns unsubscribe function
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        console.error('[MintingRegistry] Listener threw:', error);
      }
    });
  }

  /**
   * Register a new minting dapp.
   * If a dapp with the same ID already exists, it will be overwritten.
   *
   * @param dapp - The dapp to register
   */
  register(dapp: MintingDapp): void {
    if (this.dapps.has(dapp.id)) {
      console.warn(`[MintingRegistry] Dapp with id "${dapp.id}" already registered. Overwriting.`);
    }
    this.dapps.set(dapp.id, dapp);
    console.log(`[MintingRegistry] Registered dapp: ${dapp.name} (${dapp.id})`);
    this.notify();
  }

  /**
   * Unregister a minting dapp by ID.
   *
   * @param dappId - The ID of the dapp to unregister
   * @returns true if dapp was removed, false if not found
   */
  unregister(dappId: string): boolean {
    const result = this.dapps.delete(dappId);
    if (result) {
      console.log(`[MintingRegistry] Unregistered dapp: ${dappId}`);
      this.notify();
    }
    return result;
  }

  /**
   * Get all registered minting dapps.
   *
   * @returns Array of all registered dapps
   */
  getAll(): MintingDapp[] {
    return Array.from(this.dapps.values());
  }

  /**
   * Get a specific dapp by ID.
   *
   * @param dappId - The ID of the dapp to retrieve
   * @returns The dapp, or undefined if not found
   */
  get(dappId: string): MintingDapp | undefined {
    return this.dapps.get(dappId);
  }

  /**
   * Check if a dapp is registered.
   *
   * @param dappId - The ID to check
   * @returns true if dapp exists, false otherwise
   */
  has(dappId: string): boolean {
    return this.dapps.has(dappId);
  }

  /**
   * Get the count of registered dapps.
   *
   * @returns Number of registered dapps
   */
  get count(): number {
    return this.dapps.size;
  }

  /**
   * Get all dapp IDs.
   *
   * @returns Array of all dapp IDs
   */
  getIds(): string[] {
    return Array.from(this.dapps.keys());
  }

  /**
   * Get dapps by type (internal or external).
   *
   * @param type - The type to filter by
   * @returns Array of dapps matching the type
   */
  getByType(type: 'internal' | 'external'): MintingDapp[] {
    return this.getAll().filter((dapp) => dapp.type === type);
  }

  /**
   * Clear all registered dapps.
   * Useful for testing or resetting state.
   */
  clear(): void {
    this.dapps.clear();
    console.log('[MintingRegistry] Cleared all dapps');
    this.notify();
  }
}

/**
 * Global singleton registry instance.
 * Import this to register and lookup dapps throughout the app.
 */
export const mintingDappRegistry = new MintingDappRegistry();
